import { useCallback, useMemo, useState } from 'react';

import { IDocumentFile, IDocumentFilters } from './types';
import {
  combinePredicates,
  createFileTypePredicate,
  createSearchPredicate,
  getFileTypeOptions,
} from './utils';

interface IProps<FT extends IDocumentFilters, DT extends IDocumentFile> {
  records?: DT[];
  customPredicates?: Array<(record: DT, filters: FT) => boolean>;
}

export const useDocumentFilters = <
  FT extends IDocumentFilters = IDocumentFilters,
  DT extends IDocumentFile = IDocumentFile,
>({
  records = [],
  customPredicates = [],
}: IProps<FT, DT>) => {
  const [filters, setFilters] = useState<FT>({} as FT);

  const getActiveFilterCount = useCallback(
    () =>
      Object.values(filters).filter((value) => {
        if (Array.isArray(value)) {
          return value.length > 0;
        }

        return !!value;
      }).length,
    [filters]
  );

  const clearFilters = useCallback(() => {
    setFilters({} as FT);
  }, []);

  const fileTypeOptions = useMemo(() => getFileTypeOptions(records), [records]);

  const filteredRecords = useMemo(() => {
    const basePredicates = [
      createSearchPredicate(filters.search),
      createFileTypePredicate(filters.fileTypes),
    ];

    const allPredicates = [
      ...basePredicates,
      ...customPredicates.map((predicate) => (record: DT) => predicate(record, filters)),
    ];

    const combinedPredicate = combinePredicates(allPredicates);

    return records.filter(combinedPredicate);
  }, [records, filters, customPredicates]);

  return {
    filters,
    setFilters,
    getActiveFilterCount,
    clearFilters,
    filteredRecords,
    fileTypeOptions,
  };
};
